import { z } from 'zod';

import { LOCALES } from '../enums.js';

export const TenantStatusSchema = z.enum(['active', 'suspended', 'closed']);
export type TenantStatus = z.infer<typeof TenantStatusSchema>;

export const TenantPlanSchema = z.enum(['free', 'starter', 'pro', 'enterprise']);
export type TenantPlan = z.infer<typeof TenantPlanSchema>;

export const TenantBillingStatusSchema = z.enum([
  'trialing',
  'active',
  'past_due',
  'unpaid',
  'cancelled',
]);
export type TenantBillingStatus = z.infer<typeof TenantBillingStatusSchema>;

const moneyAmount = z.string().regex(/^-?\d+(\.\d{1,4})?$/, 'expected decimal amount');
const currencyCode = z.string().length(3).regex(/^[A-Z]{3}$/);

export const TenantSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  slug: z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'slug must be lowercase kebab-case'),
  defaultLocale: z.enum(LOCALES),
  baseCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/),
  status: TenantStatusSchema,
  plan: TenantPlanSchema,
  // Optional logo. Stored as a URL — pasting a CDN link or an uploaded
  // asset URL is fine. The tenant app renders this in place of the
  // generic IQ ERP mark when set.
  logoUrl: z.string().url().nullable(),
  // Billing — coarse-grained state machine that's independent of
  // `status` (which controls sign-in). A tenant can be `active` but
  // `past_due` — they can still log in, the platform admin just sees
  // a warning chip until they pay.
  billingStatus: TenantBillingStatusSchema,
  currentPeriodStart: z.string().datetime().nullable(),
  currentPeriodEnd: z.string().datetime().nullable(),
  trialEndsAt: z.string().datetime().nullable(),
  lastPaymentAt: z.string().datetime().nullable(),
  yearlyPriceAmount: moneyAmount.nullable(),
  yearlyPriceCurrencyCode: currencyCode.nullable(),
  billingNotes: z.string().max(2_000).nullable(),
  // GL posting defaults — set via PATCH /tenants/current. Required for posting
  // invoices, bills, and payments; missing values cause a 422 at post time.
  arAccountId: z.string().uuid().nullable(),
  apAccountId: z.string().uuid().nullable(),
  defaultRevenueAccountId: z.string().uuid().nullable(),
  defaultExpenseAccountId: z.string().uuid().nullable(),
  taxPayableAccountId: z.string().uuid().nullable(),
  taxReceivableAccountId: z.string().uuid().nullable(),
  defaultCashAccountId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Tenant = z.infer<typeof TenantSchema>;

export const TenantUpdateSchema = z.object({
  name: z.string().min(1).optional(),
  defaultLocale: z.enum(LOCALES).optional(),
  baseCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).optional(),
  logoUrl: z.string().url().nullable().optional(),
  arAccountId: z.string().uuid().nullable().optional(),
  apAccountId: z.string().uuid().nullable().optional(),
  defaultRevenueAccountId: z.string().uuid().nullable().optional(),
  defaultExpenseAccountId: z.string().uuid().nullable().optional(),
  taxPayableAccountId: z.string().uuid().nullable().optional(),
  taxReceivableAccountId: z.string().uuid().nullable().optional(),
  defaultCashAccountId: z.string().uuid().nullable().optional(),
});

export type TenantUpdate = z.infer<typeof TenantUpdateSchema>;
